// 122 — Transfusión Sanguínea, Registro y Seguimiento (Anexo 1 MSP, SNS-MSP/HCU-form.122/2021). Fase 7, Tanda 6:
// las dos hojas del papel en un solo formulario (un solo código 122, como decidió el informe de brechas): Registro
// (servicio y prioridad, componentes enviados, pruebas pretransfusionales, responsables del traslado) y Seguimiento
// (tomas antes/durante/después, notificación de reacción adversa, responsables). Campos y decisiones en
// `bloques122Datos.ts`. El bloque A lo resuelve `EncabezadoMspCampos`.
import { useMedplum } from '@medplum/react-hooks';
import type { Procedure, ServiceRequest } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { AreaDictado } from './AreaDictado';
import { Bloque, CamposSimples } from './camposSimples';
import { SelectorDiagnosticoCie10 } from './SelectorDiagnosticoCie10';
import { SelectorRecurso } from './SelectorRecurso';
import { TablaConColumnas } from './uci/TablaConColumnas';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { reasonReferenceDeCondicion } from '../recetas/reasonReference';
import { observacionesDeTomas } from './signosVitalesToma';
import type { CampoSimple } from './camposSimplesDatos';
import {
  CAMPOS_PRETRANSFUSIONALES_122,
  CAMPOS_RAT_IDENTIFICACION_122,
  CAMPOS_RAT_MOMENTO_122,
  CAMPOS_RAT_SIGNOS_122,
  CAMPOS_REACCIONES_PREVIAS_122,
  CAMPOS_RESPONSABLES_122,
  CAMPOS_SERVICIO_122,
  CAMPOS_TOTAL_OBSERVACIONES_122,
  CAMPOS_TRASLADO_122,
  COLUMNAS_COMPONENTES_122,
  COLUMNAS_SEGUIMIENTO_122,
  estadoInicial122,
  FILAS_COMPONENTES_122,
  FILAS_SEGUIMIENTO_122,
  paresDe122,
  resumenRat122,
  tomasDe122,
  textoProcedimiento122,
  totalObservacionesEfectivo122,
  type Estado122,
} from './bloques122Datos';

const CAMPO_TOTAL = CAMPOS_TOTAL_OBSERVACIONES_122.filter((c) => c.tipo !== 'area');
const CAMPO_OBSERVACIONES = CAMPOS_TOTAL_OBSERVACIONES_122.find((c) => c.tipo === 'area')!;

export function Formulario122({ subject, encounter, conditionId, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [solicitud, setSolicitud] = useState<ServiceRequest>();
  const [estado, setEstado] = useState<Estado122>(() => estadoInicial122());
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '122', t('formularios.f122.titulo'), { servicio: estado.servicio.servicio });

  const simples = (id: string, lista: readonly CampoSimple[], clave: keyof Estado122, columnas: 2 | 3 | 4): JSX.Element => (
    <CamposSimples prefijoId={`f122-${id}`} campos={lista} valores={estado[clave] as Record<string, string>} columnas={columnas} onChange={(v) => setEstado({ ...estado, [clave]: v })} />
  );

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const lote = new LoteTransaccional();
      const ahora = new Date().toISOString();
      const observaciones = (estado.totalObservaciones.observaciones ?? '').trim();
      const rat = resumenRat122(estado);
      const notas = [...(observaciones ? [`Observaciones: ${observaciones}`] : []), ...(rat ? [rat] : [])];
      lote.crear<Procedure>({
        resourceType: 'Procedure',
        status: 'completed',
        code: { text: textoProcedimiento122(estado) },
        subject,
        encounter,
        performedDateTime: ahora,
        basedOn: solicitud?.id ? [{ reference: `ServiceRequest/${solicitud.id}` }] : undefined,
        note: notas.map((text) => ({ text })),
        reasonReference: reasonReferenceDeCondicion(conditionId),
      });
      // Las tomas antes/durante/después se fechan con la hora de inicio / hora finalizada de su fila (el papel no trae hora por toma; ver `tomasDe122`).
      observacionesDeTomas(tomasDe122(estado, ahora), subject, encounter, ahora).forEach((o) => lote.crear(o));
      const pares = [...(solicitud ? [{ pregunta: t('formularios.f122.solicitudOrigen'), respuesta: solicitud.code?.text ?? solicitud.id ?? '' }] : []), ...paresDe122(estado)];
      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f122.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <SelectorRecurso<'ServiceRequest'>
        resourceType="ServiceRequest"
        subject={subject}
        label={t('formularios.f122.solicitudOrigen')}
        etiqueta={(r) => r.code?.text ?? r.id ?? ''}
        value={solicitud}
        onChange={setSolicitud}
      />

      <h4 className="text-sm font-semibold text-hce-ink">{t('formularios.f122.registroTitulo')}</h4>

      <Bloque titulo={t('formularios.f122.servicioTitulo')}>
        <SelectorDiagnosticoCie10 label={t('formularios.f122.diagnosticoPrincipal')} value={estado.diagnosticoPrincipal} onChange={(v) => setEstado({ ...estado, diagnosticoPrincipal: v })} />
        {simples('srv', CAMPOS_SERVICIO_122, 'servicio', 3)}
        <SelectorDiagnosticoCie10 label={t('formularios.f122.diagnosticoTransfusion')} value={estado.diagnosticoTransfusion} onChange={(v) => setEstado({ ...estado, diagnosticoTransfusion: v })} />
        {simples('prev', CAMPOS_REACCIONES_PREVIAS_122, 'reaccionesPrevias', 2)}
      </Bloque>

      <Bloque titulo={t('formularios.f122.componentesTitulo')}>
        <TablaConColumnas filas={FILAS_COMPONENTES_122} columnas={COLUMNAS_COMPONENTES_122} estado={estado.componentes} onChange={(c) => setEstado({ ...estado, componentes: c })} />
        <p className="text-sm font-medium text-hce-ink">{t('formularios.f122.pretransfusionales')}</p>
        {simples('pre', CAMPOS_PRETRANSFUSIONALES_122, 'pretransfusionales', 4)}
        <CamposSimples prefijoId="f122-tot" campos={CAMPO_TOTAL} valores={totalObservacionesEfectivo122(estado)} columnas={2} onChange={(v) => setEstado({ ...estado, totalObservaciones: { ...estado.totalObservaciones, ...v } })} />
        <AreaDictado id="f122-observaciones" etiqueta={CAMPO_OBSERVACIONES.etiqueta} valor={estado.totalObservaciones.observaciones ?? ''} filas={3} onChange={(v) => setEstado({ ...estado, totalObservaciones: { ...estado.totalObservaciones, observaciones: v } })} />
      </Bloque>

      <Bloque titulo={t('formularios.f122.trasladoTitulo')}>{simples('tras', CAMPOS_TRASLADO_122, 'traslado', 3)}</Bloque>

      <h4 className="text-sm font-semibold text-hce-ink">{t('formularios.f122.seguimientoTitulo')}</h4>

      <Bloque titulo={t('formularios.f122.procesoTitulo')}>
        <TablaConColumnas filas={FILAS_SEGUIMIENTO_122} columnas={COLUMNAS_SEGUIMIENTO_122} estado={estado.seguimiento} onChange={(c) => setEstado({ ...estado, seguimiento: c })} />
      </Bloque>

      <Bloque titulo={t('formularios.f122.ratTitulo')}>
        {simples('ratm', CAMPOS_RAT_MOMENTO_122, 'ratMomento', 2)}
        <p className="text-sm font-medium text-hce-ink">{t('formularios.f122.ratSignos')}</p>
        {simples('rats', CAMPOS_RAT_SIGNOS_122, 'ratSignos', 4)}
        <p className="text-sm font-medium text-hce-ink">{t('formularios.f122.ratIdentificacion')}</p>
        {simples('rati', CAMPOS_RAT_IDENTIFICACION_122, 'ratIdentificacion', 3)}
      </Bloque>

      <Bloque titulo={t('formularios.f122.responsablesTitulo')}>{simples('resp', CAMPOS_RESPONSABLES_122, 'responsables', 2)}</Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
